import ImageUploadIcon from "@Icons/ImageUploadIcon";
import style from "../AddReview.module.scss";
import { useEffect, useState } from "react";
import ImageRemoveButton from "@Icons/ImageRemoveButton";
import Image from "@Components/Image";
import ToastNotificationDispatcher from "@Components/ToastNotification/actions";
import { useDispatch, useSelector } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import { AppState } from "@Store/index";
import { toVideoCDNUrl } from "helpers/videoHelper";
import { uploadReviewImages, fetchUploadCredentials, uploadToS3 } from "../../data/helper";
import { logger } from "helpers/logger";

import LoaderIcon from "@Icons/LoaderIcon";
import { MediaFile } from "@Pages/AmberPlus/constants/types";
import useTranslationHook from "hooks/useTranslationHook";
//

const ImagePicker = ({ setReview, review, hasExistingReview = false, userBooking = {} }: any) => {
  const MAX_FILE_SIZE = 200 * 1024 * 1024;
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const toastNotificationDispatcher = new ToastNotificationDispatcher(dispatch);
  const [mediaUploading, setMediaUploading] = useState(false);

  const [selectedImages, setSelectedImages] = useState<MediaFile[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<MediaFile[]>([]);

  function isFileTypeAllowed(file: File) {
    const allowedTypes = [
      "image/png",
      "image/jpg",
      "image/jpeg",
      "image/webp",
      "image/avif",
      "image/tiff",
      "video/quicktime",
      "video/mp4",
    ];

    if (!file) {
      throw new Error("No file provided");
    }

    return allowedTypes.includes(file.type);
  }

  function uuid(): string {
    const now = Date.now().toString(36);
    const rand = Math.random().toString(36).substring(2, 10);
    const perf =
      typeof performance !== "undefined" && performance.now ? performance.now().toString(36) : "";
    return `${now}-${rand}-${perf}`;
  }

  const { id } = useSelector(({ propertyPageData }: AppState) => ({
    id: propertyPageData?.data?.id || userBooking?.id,
  }));

  const handleFileChange = (event: any) => {
    const files = Array.from(event.target.files);
    event.target.value = "";

    if (files.length > 10) {
      toastNotificationDispatcher.openToast({
        title: "propertypage.reviewssection.addreview.addreviewform.errortitle",
        message: "propertypage.reviewssection.addreview.addreviewform.maxfileserror",
        type: "error",
        color: "red",
      });
      return;
    }

    const newImages: MediaFile[] = [];
    const newVideos: MediaFile[] = [];

    for (const raw of files) {
      const file = raw as File;
      if (!isFileTypeAllowed(file)) {
        toastNotificationDispatcher.openToast({
          title: "propertypage.reviewssection.addreview.addreviewform.errortitle",
          message: "propertypage.reviewssection.addreview.addreviewform.invalidfiletype",
          type: "error",
          color: "red",
        });
      }

      if (file.size > MAX_FILE_SIZE) {
        toastNotificationDispatcher.openToast({
          title: "propertypage.reviewssection.addreview.addreviewform.errortitle",
          message: "propertypage.reviewssection.addreview.addreviewform.filesizeexceeded",
          type: "error",
          color: "red",
        });
      }

      if (file.type.startsWith("image/")) {
        if (!selectedImages.some((img) => img.file.name === file.name)) {
          newImages.push({
            file,
            status: "pending",
            id: uuid(),
            previewUrl: URL.createObjectURL(file),
          });
        }
      } else if (file.type.startsWith("video/")) {
        if (!selectedVideos.some((vid) => vid.file.name === file.name)) {
          newVideos.push({
            file,
            status: "pending",
            id: uuid(),
            previewUrl: URL.createObjectURL(file),
          });
        }
      }
    }

    const prefilledImagesCount = hasExistingReview ? review?.images?.length || 0 : 0;
    const prefilledVideosCount = hasExistingReview ? review?.videos?.length || 0 : 0;
    const totalExisting =
      selectedImages.length + selectedVideos.length + prefilledImagesCount + prefilledVideosCount;
    if (totalExisting + newImages.length + newVideos.length > 10) {
      toastNotificationDispatcher.openToast({
        title: "propertypage.reviewssection.addreview.addreviewform.errortitle",
        message: "propertypage.reviewssection.addreview.addreviewform.maxfileserror",
        type: "error",
        color: "red",
      });
      return;
    }
    setSelectedImages((prev) => [...prev, ...newImages]);
    setSelectedVideos((prev) => [...prev, ...newVideos]);
  };

  useEffect(() => {
    if (!hasExistingReview) {
      return;
    }

    const needsInit =
      (Array.isArray(review?.images) &&
        review.images.length > 0 &&
        review.images.some((img: any) => !img?.id)) ||
      (Array.isArray(review?.videos) &&
        review.videos.length > 0 &&
        review.videos.some((vid: any) => !vid?.id));

    if (!needsInit) {
      return;
    }

    setReview((prev: any) => {
      const withIds = (arr: any[] = []) => arr.map((m: any) => ({ ...m, id: m?.id || uuid() }));
      return {
        ...prev,
        images: withIds(prev?.images),
        videos: withIds(prev?.videos),
      };
    });
  }, [hasExistingReview, review?.images, review?.videos]);

  useEffect(() => {
    const uploadMedia = async () => {
      setMediaUploading(true);

      const imageUploadPromises = selectedImages.map(async (image, i) => {
        if (image.status !== "pending") return;

        setSelectedImages((prev) => {
          const copy = [...prev];
          copy[i] = { ...copy[i], status: "uploading" };
          return copy;
        });

        try {
          const { file } = image;
          const res = await uploadReviewImages(file.name, {}, id);
          const uploadRes = await uploadToS3(res, file);

          setReview((prev: any) => ({
            ...prev,
            images: [...(prev.images || []), { id: image.id, path: uploadRes, type: file.type }],
          }));

          setSelectedImages((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], status: "uploaded" };
            return copy;
          });
        } catch (err) {
          logger.error("Image upload failed", err);
        }
      });

      const videoUploadPromises = selectedVideos.map(async (video, i) => {
        if (video.status !== "pending") return;

        setSelectedVideos((prev) => {
          const copy = [...prev];
          copy[i] = { ...copy[i], status: "uploading" };
          return copy;
        });

        try {
          const { file } = video;
          let fileName = file.name;
          if (!fileName) {
            if (file && file.type) {
              const ext = file.type.split("/")[1] || "mp4";
              fileName = `video_${Date.now()}.${ext}`;
            } else {
              fileName = `video_${Date.now()}.mp4`;
            }
          }
          const res = await fetchUploadCredentials(fileName);
          const s3Key = await uploadToS3(res, file, true);

          const videoCDNUrl = toVideoCDNUrl(s3Key);
          const thumbnail_url = videoCDNUrl
            .replace("main.mp4", "thumbnail.jpg")
            .replace(".mp4", ".jpg");

          setReview((prev: any) => ({
            ...prev,
            videos: [
              ...(prev.videos || []),
              { id: video.id, path: videoCDNUrl, type: file.type, thumbnail_url },
            ],
          }));

          setSelectedVideos((prev) => {
            const copy = [...prev];
            copy[i] = { ...copy[i], status: "uploaded" };
            return copy;
          });
        } catch (err) {
          logger.error("Video upload failed", err);
        }
      });

      await Promise.all([...imageUploadPromises, ...videoUploadPromises]);
      setMediaUploading(false);
    };

    uploadMedia();
  }, [selectedImages, selectedVideos]);

  useEffect(
    () => () => {
      selectedImages.forEach((item) => URL.revokeObjectURL(item.previewUrl));
      selectedVideos.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    },
    [],
  );

  const removeFile = (fileId: string, type: "images" | "videos") => {
    if (type === "images") {
      setSelectedImages((prev) => {
        const target = prev.find((item) => item.id === fileId);
        if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);

        const updated = prev.filter((item) => item.id !== fileId);

        setReview((prevReview: any) => {
          const newImages = (prevReview.images || []).filter((img: any) => img.id !== fileId);
          return {
            ...prevReview,
            imageFiles: updated,
            images: newImages,
          };
        });

        return updated;
      });
    } else {
      setSelectedVideos((prev) => {
        const target = prev.find((item) => item.id === fileId);
        if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);

        const updated = prev.filter((item) => item.id !== fileId);

        setReview((prevReview: any) => {
          const newVideos = (prevReview.videos || []).filter((vid: any) => vid.id !== fileId);
          return {
            ...prevReview,
            videoFiles: updated,
            videos: newVideos,
          };
        });

        return updated;
      });
    }
  };

  const removeExistingFile = (idOrPath: string, type: "images" | "videos") => {
    setReview((prevReview: any) => {
      const updated = (prevReview?.[type] || []).filter(
        (item: any) => (item?.id || item?.path) !== idOrPath,
      );
      return {
        ...prevReview,
        [type]: updated,
      };
    });
  };

  useEffect(() => {
    Promise.resolve().then(() => {
      setReview((prevReview: any) => ({
        ...prevReview,
        imageFiles: selectedImages,
        videoFiles: selectedVideos,
      }));
    });
  }, [selectedImages, selectedVideos]);

  return (
    <>
      <div className={style.imagesDiv}>
        <label htmlFor="images" className={style.uploadButton}>
          <input
            type="file"
            id="images"
            onChange={handleFileChange}
            name="images"
            multiple
            style={{ display: "none" }}
          />
          <ImageUploadIcon />
          <span className={style.uploadButtonText}>
            {t("propertypage.reviewssection.addreview.addreviewform.addphoto")}
          </span>
        </label>
        {/* Existing images from review */}
        {hasExistingReview &&
          (review?.images || []).map((img: any, idx: number) => (
            <div className={style.imageContainer} key={`existing-img-${img?.id || idx}`}>
              <button
                onClick={() => removeExistingFile(img?.id || img?.path, "images")}
                className={style.removeFile}
                type="button"
                disabled={mediaUploading}
              >
                <span style={{ display: "none" }}>txt</span>
                <ImageRemoveButton />
              </button>
              <Image src={img.path} height="100%" width="100%" alt="Selected" />
            </div>
          ))}
        {/* Existing videos from review */}
        {hasExistingReview &&
          (review?.videos || []).map((vid: any, idx: number) => (
            <div className={style.imageContainer} key={`existing-vid-${vid?.id || idx}`}>
              <button
                onClick={() => removeExistingFile(vid?.id || vid?.path, "videos")}
                className={style.removeFile}
                type="button"
                disabled={mediaUploading}
              >
                <span style={{ display: "none" }}>txt</span>
                <ImageRemoveButton />
              </button>
              <video style={{ height: "100%", width: "100%" }}>
                <track default kind="captions" src="" />
                <source src={vid.path} type="video/mp4" />
              </video>
              <div className={style.playButtonContainer}>
                <div className={style.playButton}>
                  <div className={style.playButtonTriangle} />
                </div>
              </div>
            </div>
          ))}
        {selectedImages.map((imageObj) => (
          <div className={style.imageContainer} key={imageObj.id}>
            <button
              onClick={() => removeFile(imageObj.id, "images")}
              className={style.removeFile}
              type="button"
              disabled={mediaUploading}
            >
              <span style={{ display: "none" }}>txt</span>
              <ImageRemoveButton />
            </button>
            <Image src={imageObj.previewUrl} height="100%" width="100%" alt="Selected" />
            {imageObj.status === "uploading" && (
              <div className={style.uploadOverlay}>
                <LoaderIcon fill=" #e53863" />
              </div>
            )}
          </div>
        ))}

        {selectedVideos.map((videoObj) => (
          <div className={style.imageContainer} key={videoObj.id}>
            <button
              onClick={() => removeFile(videoObj.id, "videos")}
              className={style.removeFile}
              type="button"
              disabled={mediaUploading}
            >
              <span style={{ display: "none" }}>txt</span>
              <ImageRemoveButton />
            </button>
            <video style={{ height: "100%", width: "100%" }}>
              <track default kind="captions" src="" />
              <source src={videoObj.previewUrl} type="video/mp4" />
            </video>
            <div className={style.playButtonContainer}>
              <div className={style.playButton}>
                <div className={style.playButtonTriangle} />
              </div>
            </div>
            {videoObj.status === "uploading" && (
              <div className={style.uploadOverlay}>
                <LoaderIcon fill=" #e53863" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* <div className={style.maxLimit}>Min. 1 photo, Max. 10 photos</div> */}
    </>
  );
};

export default wrapperHOC(ImagePicker, {
  componentName: "ImagePicker-ReviewsSection",
  showForChina: true,
});
